/*
 * SF SUB-BUNDLE PICKER
 *
 * Adds two plan-bearing component lines in one call, then proves they landed
 * correctly before telling the shopper anything succeeded.
 *
 * Every id, quantity, plan id and price comes from data attributes rendered by
 * snippets/sf-sub-bundle-picker.liquid. Nothing here is hardcoded and nothing
 * here formats currency: Liquid renders every money string on the card and the
 * script never rewrites one, so the card cannot drift from the page.
 *
 * A custom element rather than an inline script, deliberately: product-info
 * allows "liquid" blocks in the theme's partial re-render, and theme.js swaps
 * them in from a parsed fragment whose <script> elements never execute. Custom
 * element upgrade runs on connection, so this re-arms itself for free.
 *
 * REFUSE IF PRESENT. If either component variant is already in the cart this
 * does not add -- EXCEPT when the cart already holds a clean whole-bundle
 * multiple of BOTH components on this plan, which bundleMultiple() can prove is
 * ours to extend; that case bumps to the next multiple instead of refusing.
 * These are public products a shopper may already subscribe to, and an earlier
 * draft that matched "our" lines by variant and plan would have deleted a real
 * customer's subscription on rollback. Because the pre-check guarantees the
 * cart held either nothing of ours or a clean multiple of it, rolling our own
 * lines back is unambiguous. Tagging lines with a property would also
 * disambiguate them and is forbidden: UpCart wipes the cart when a subscription
 * line carries one -- which is also why the CART snippet no longer removes any
 * line automatically (see its header).
 *
 * VERIFY PER LINE, NEVER BY CART TOTAL. cart.total_price is post-discount and
 * this store runs a live automatic discount, so a total-delta assertion fails
 * falsely and would trigger the rollback. Each line is checked on its own:
 * plan id, quantity, and selling_plan_allocation.price (NOT line.price, which
 * is discount-sensitive).
 */
(function () {
  'use strict';

  var TAG = 'sf-sub-bundle-picker';
  if (window.customElements && window.customElements.get(TAG)) return;

  var inFlight = false;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // Number('') is 0 and Number.isFinite(0) is true, so an empty attribute would
  // sail through a naive check and post quantity 0 or selling_plan 0.
  function toInt(value) {
    if (typeof value !== 'string') return null;
    var trimmed = value.trim();
    if (!/^\d+$/.test(trimmed)) return null;
    var n = Number(trimmed);
    return Number.isSafeInteger(n) ? n : null;
  }

  function getJSON(url, init) {
    return fetch(url, init).then(function (res) {
      return res.json().then(
        function (body) { return { ok: res.ok, status: res.status, body: body }; },
        function () { return { ok: res.ok, status: res.status, body: null }; }
      );
    });
  }

  function postJSON(url, payload) {
    return getJSON(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  function codesOn(cart) {
    return ((cart && cart.discount_codes) || []).filter(function (d) { return d && d.applicable !== false; });
  }

  function linesFor(cart, variantId) {
    var out = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].id === variantId) out.push(items[i]);
    }
    return out;
  }

  function planOk(line, planId) {
    if (!line) return false;
    var alloc = line.selling_plan_allocation;
    if (!alloc || !alloc.selling_plan) return false;
    return Number(alloc.selling_plan.id) === planId;
  }

  // MULTIPLE-AWARE. Before the stepper this compared against spec.qty alone,
  // which silently became wrong the moment a click could post more than one
  // bundle: at N=3 the line holds qty*3, the equality failed, `attributable`
  // went false, and a PLAN FAILURE WOULD NOT HAVE BEEN ROLLED BACK -- leaving
  // exactly the unverified lines this check exists to remove.
  function qtyOk(line, spec, multiple) {
    return !!line && line.quantity === spec.qty * (multiple || 1);
  }

  // alloc.price is the plan's own price, not line.price — line.price moves with
  // any automatic discount and would fail against this store's live promo.
  //
  // Whether the Ajax cart reports alloc.price per UNIT or per LINE on a
  // quantity-5 line is not something the repo can settle offline, so both
  // readings are accepted. A price mismatch is also deliberately NOT a
  // rollback trigger (see verify()): if this interpretation were wrong, an
  // auto-rollback would delete a perfectly correct cart on every single add.
  // The per-LINE reading has to scale with the multiple too. Before the stepper
  // the line always held exactly spec.qty, so `allocPrice * spec.qty` covered it;
  // at N=3 a line-scaled response is allocPrice * qty * 3 and would have been
  // read as a mismatch, collapsing bundleMultiple to 0 on a perfectly good cart.
  function priceOk(line, spec, multiple) {
    if (!line || !line.selling_plan_allocation) return false;
    var p = Number(line.selling_plan_allocation.price);
    return p === spec.allocPrice || p === spec.allocPrice * spec.qty * (multiple || 1);
  }

  function Picker() {
    return Reflect.construct(HTMLElement, [], Picker);
  }
  Picker.prototype = Object.create(HTMLElement.prototype);
  Picker.prototype.constructor = Picker;
  Object.setPrototypeOf(Picker, HTMLElement);

  Picker.prototype.connectedCallback = function () {
    this.button = this.querySelector('[data-sfb-atc]');
    this.labelEl = this.querySelector('[data-sfb-atc-label]');
    this.errorEl = this.querySelector('[data-sfb-error]');
    if (!this.button || !this.labelEl) return;

    var d = this.dataset;
    this.planId = toInt(d.planId);
    this.filter = { id: toInt(d.filterVariantId), qty: toInt(d.filterQty), allocPrice: toInt(d.filterAllocPrice) };
    this.refill = { id: toInt(d.refillVariantId), qty: toInt(d.refillQty), allocPrice: toInt(d.refillAllocPrice) };

    var valid = this.planId !== null &&
      this.filter.id !== null && this.filter.qty !== null && this.filter.allocPrice !== null &&
      this.refill.id !== null && this.refill.qty !== null && this.refill.allocPrice !== null;

    if (!valid) {
      // Config we cannot trust. Stay disabled and say nothing misleading.
      this.button.disabled = true;
      return;
    }

    // Quantity is optional: if the stepper markup is absent the picker behaves
    // exactly as it did before it existed, adding one bundle per click.
    this.qtyInput = this.querySelector('[data-sfb-qty]');
    this.qtyMax = toInt(d.qtyMax) || 10;

    if (!this.wired) {
      this.onClick = this.handleClick.bind(this);
      this.button.addEventListener('click', this.onClick);

      if (this.qtyInput) {
        // `input` as well as change/blur: clampQty now drives only appearance,
        // and appearance should track what the field says while it is being
        // typed into, not a beat later.
        this.onQtyInput = this.clampQty.bind(this);
        this.qtyInput.addEventListener('input', this.onQtyInput);
        this.qtyInput.addEventListener('change', this.onQtyInput);
        this.qtyInput.addEventListener('blur', this.onQtyInput);

        // The step direction is a WORD, not a signed number, because toInt()
        // accepts /^\d+$/ only -- it rejects the minus sign, so toInt('-1')
        // returned null and the decrement button stepped by zero, i.e. did
        // nothing at all. Words cannot fall into that hole.
        this.onQtyStep = function (event) {
          var btn = event.target.closest('[data-sfb-qty-step]');
          if (!btn) return;
          var dir = btn.getAttribute('data-sfb-qty-step');
          if (dir !== 'inc' && dir !== 'dec') return;
          this.qtyInput.value = String(this.readQty() + (dir === 'inc' ? 1 : -1));
          this.clampQty();
        }.bind(this);
        this.addEventListener('click', this.onQtyStep);
      }

      this.wired = true;
    }

    this.clampQty();

    // A re-render mid-add must not hand the shopper a live button while the
    // previous chain is still running against a now-detached node.
    if (inFlight) {
      this.button.disabled = true;
      return;
    }

    this.setLabel(this.dataset.labelIdle);
    this.button.disabled = false;
    this.reconcile();
  };

  Picker.prototype.disconnectedCallback = function () {
    if (this.wired && this.onClick) {
      this.button.removeEventListener('click', this.onClick);

      // Every listener added under the same `wired` flag must come off here.
      // disconnectedCallback clears the flag, so connectedCallback re-adds them
      // on the next attach -- anything left behind is silently duplicated, and a
      // doubled step listener would move the quantity by two per click.
      if (this.qtyInput && this.onQtyInput) {
        this.qtyInput.removeEventListener('input', this.onQtyInput);
        this.qtyInput.removeEventListener('change', this.onQtyInput);
        this.qtyInput.removeEventListener('blur', this.onQtyInput);
      }
      if (this.onQtyStep) this.removeEventListener('click', this.onQtyStep);

      this.wired = false;
    }
  };

  // How many whole bundles these two lines represent, or 0 if they are not a
  // clean bundle. Both lines must be on the plan, at the right unit price, and
  // at quantities that are the SAME exact multiple of their per-bundle counts.
  // Anything else — a half bundle, an odd quantity, a line the shopper built
  // themselves — returns 0 and is never touched.
  // HOW MANY BUNDLES THIS CLICK ADDS. Always at least 1, so a missing, blank or
  // junk stepper value degrades to the pre-stepper behaviour rather than to 0 or
  // NaN, either of which would post a nonsense quantity.
  Picker.prototype.readQty = function () {
    if (!this.qtyInput) return 1;
    var n = toInt(this.qtyInput.value);
    if (n === null || n < 1) return 1;
    if (n > this.qtyMax) return this.qtyMax;
    return n;
  };

  // The min/max attributes are a hint, not a guarantee -- the field can be typed
  // into and pasted into. Clamp the displayed value to what will actually be
  // used, so the control never shows a number the picker will silently ignore.
  // THE BUTTONS ARE NEVER `disabled`, AND THAT IS THE POINT.
  //
  // They used to be, and it produced a real bug: add 4 to the cart, close the
  // drawer, and the minus button was stuck greyed out at 4 -- clicking plus
  // once un-stuck it. `disabled` is FUNCTIONAL state maintained by JS, so any
  // path where this function fails to re-run at the right moment leaves a
  // permanently dead control. Around a third-party cart drawer that re-renders
  // the page on its own schedule, that is not a bug to chase, it is a bug to
  // design out.
  //
  // So the bound is enforced where it cannot go stale -- readQty() clamps on
  // every read, and a click at the limit is simply a no-op. Appearance is
  // carried by a CLASS instead, so the worst case for a missed update is a
  // button that looks greyed while still working, rather than one that looks
  // greyed and IS dead. aria-disabled keeps the state announced without
  // removing the control from the tab order or swallowing its clicks.
  Picker.prototype.clampQty = function () {
    if (!this.qtyInput) return;
    var n = this.readQty();
    if (this.qtyInput.value !== String(n)) this.qtyInput.value = String(n);
    this.markBound('[data-sfb-qty-step="dec"]', n <= 1);
    this.markBound('[data-sfb-qty-step="inc"]', n >= this.qtyMax);
  };

  Picker.prototype.markBound = function (selector, atBound) {
    var el = this.querySelector(selector);
    if (!el) return;
    el.classList.toggle('sfb-qty-btn--bound', atBound);
    el.setAttribute('aria-disabled', atBound ? 'true' : 'false');
  };

  Picker.prototype.bundleMultiple = function (fLines, rLines) {
    if (!fLines || !rLines || fLines.length !== 1 || rLines.length !== 1) return 0;
    var f = fLines[0], r = rLines[0];
    if (!planOk(f, this.planId) || !planOk(r, this.planId)) return 0;
    if (!this.filter.qty || !this.refill.qty) return 0;
    if (f.quantity % this.filter.qty !== 0 || r.quantity % this.refill.qty !== 0) return 0;
    var mf = f.quantity / this.filter.qty;
    var mr = r.quantity / this.refill.qty;
    if (mf < 1 || mf !== mr) return 0;
    // Price is checked AFTER the multiple is known, because the per-line reading
    // of alloc.price scales with it. Checking first, as this did before the
    // stepper, meant the price test could only ever be right at a multiple of 1.
    if (!priceOk(f, this.filter, mf) || !priceOk(r, this.refill, mf)) return 0;
    return mf;
  };

  Picker.prototype.setLabel = function (text) {
    if (text) this.labelEl.textContent = text;
  };

  Picker.prototype.clear = function () {
    if (this.errorEl) this.errorEl.textContent = '';
  };

  Picker.prototype.fail = function (text) {
    if (this.errorEl) this.errorEl.textContent = text || '';
  };

  // If the bundle is already sitting in the cart correctly — because the shopper
  // added it and the block was then re-rendered — show that rather than offering
  // a second add. Only a cart that matches this component's exact spec counts.
  Picker.prototype.reconcile = function () {
    var self = this;
    getJSON(root() + 'cart.js').then(function (res) {
      if (inFlight) return;
      if (!res.ok || !res.body) return;
      var mult = self.bundleMultiple(
        linesFor(res.body, self.filter.id),
        linesFor(res.body, self.refill.id)
      );
      if (mult) {
        // The card advertises ONE bundle and never mirrors the cart, so nothing
        // here changes. Just keep the button live - another click buys another.
        self.button.disabled = false;
      }
    }).catch(function () { /* a failed reconcile leaves the button usable */ });
  };

  Picker.prototype.handleClick = function () {
    if (inFlight) return;
    inFlight = true;

    var self = this;
    this.button.disabled = true;

    var release = function () { inFlight = false; };

    // Once the add POST has left, we can no longer honestly claim "nothing was
    // added" — a dropped response is indistinguishable from a failed one.
    var addIssued = false;
    var wasBump = false;
    var beforeCodes = [];
    var beforeCodesRaw = [];
    // How many whole bundles the cart should hold once this click lands. Carried
    // so verification can assert the ACTUAL total, not merely that some valid
    // bundle exists -- asking for 3 and receiving 2 used to report success.
    var expected = 1;

    getJSON(root() + 'cart.js')
      .then(function (before) {
        if (!before.ok || !before.body) throw new Error('cart-read-failed');

        beforeCodes = codesOn(before.body);
        // RAW, not filtered. codesOn() drops codes Shopify reports as
        // applicable:false, and that filtering is right for the foreign-code
        // guard -- an inapplicable stranger's code is not worth protecting a
        // slot for. It is WRONG for deciding authorship: a shopper who types
        // the promo code in BEFORE the bundle exists has a cart under the
        // offer's minimum, so Shopify marks their own code inapplicable, it
        // vanishes from the filtered list, and we would conclude we applied it
        // ourselves and later clear it out from under them.
        beforeCodesRaw = ((before.body && before.body.discount_codes) || []);

        var pf = linesFor(before.body, self.filter.id);
        var pr = linesFor(before.body, self.refill.id);
        var mult = self.bundleMultiple(pf, pr);
        var want = self.readQty();

        // Already holding OUR bundle, whole and correct: a further click buys
        // more rather than being refused. Only ever reached when the quantities
        // are exact multiples of the bundle on both lines, so we are never
        // scaling something the shopper assembled themselves.
        if (mult) {
          // THE CAP IS BEST-EFFORT, NOT A GUARANTEE, and must not be described
          // as one. It is enforced by reading the cart and then writing to it,
          // and those two steps are not atomic. Two tabs can each read an empty
          // cart and each add 6; Shopify merges them into 12. `inFlight` is a
          // per-document flag and does not span tabs. This is the same two-tab
          // merge already acknowledged further down in the rollback comment, so
          // it is a known property of this file rather than a new hazard.
          //
          // Exact verification DOES catch the result -- 12 !== 6 reports
          // unverified and stops -- but it reports; it does not correct. A hard
          // ceiling has to be enforced where the cart actually lives: a Shopify
          // variant quantity rule or a checkout validation function. Reported,
          // not faked here.
          //
          // The cap applies to the CART TOTAL, not to one click, or ten clicks
          // of "1" would walk straight past it.
          //
          // REFUSE rather than clamp. Clamping looked kinder but lied: with 8 in
          // the cart and the stepper on 5 it would add 2, report success, and
          // leave the control still reading 5 -- silently contradicting the
          // stated "how many this click adds". Refusing keeps the control and
          // the outcome in agreement.
          if (mult + want > self.qtyMax) {
            self.button.disabled = false;
            self.fail(self.dataset.msgMax);
            return null;
          }
          expected = mult + want;
          addIssued = true;
          wasBump = true;
          var updates = {};
          updates[pf[0].key] = self.filter.qty * expected;
          updates[pr[0].key] = self.refill.qty * expected;
          return postJSON(root() + 'cart/update.js', { updates: updates });
        }

        // Either variant present but NOT as a clean bundle — someone else's
        // line, or a half bundle. Refuse rather than scale what we cannot
        // account for. Not an error; the shopper simply has one already.
        if (pf.length || pr.length) {
          self.button.disabled = false;
            self.fail(self.dataset.msgPresent);
          return null;
        }

        expected = want;
        addIssued = true;
        return postJSON(root() + 'cart/add.js', {
          items: [
            { id: self.filter.id, quantity: self.filter.qty * want, selling_plan: self.planId },
            { id: self.refill.id, quantity: self.refill.qty * want, selling_plan: self.planId }
          ]
        });
      })
      .then(function (added) {
        if (added === null) return null;
        if (!added.ok) throw new Error('add-failed');
        return getJSON(root() + 'cart.js');
      })
      .then(function (after) {
        if (after === null) return null;
        if (!after.ok || !after.body) throw new Error('verify-read-failed');

        var f = linesFor(after.body, self.filter.id);
        var r = linesFor(after.body, self.refill.id);
        var single = f.length === 1 && r.length === 1;
        var mult = self.bundleMultiple(f, r);

        // Must match the total we INTENDED, not merely be a valid bundle of some
        // size. Accepting any positive multiple meant a request for 3 that came
        // back as 2 was reported as a success.
        if (mult === expected) {
          return self.applyDiscount(beforeCodes, beforeCodesRaw).then(function () {
            // Deliberately NOT locked: another click buys another bundle.
            self.button.disabled = false;
            self.clear();
            self.openCart();
            return null;
          });
        }

        // Undo ONLY when we can prove these two lines are exactly what we just
        // posted — one line per variant, each at our own quantity. Under a
        // two-tab race Shopify merges into one line at double quantity, and
        // zeroing it would delete the other tab's order.
        //
        // And only for a PLAN failure. A missing or wrong selling plan is the
        // silent-one-time-charge defect and must not be left in the cart. A
        // price-only mismatch is reported, never auto-deleted: the cart's price
        // shape is the one thing not verifiable offline, so a wrong assumption
        // there would otherwise wipe a correct cart on every attempt.
        // A BUMP is never rolled back to zero. The shopper already had one or
        // more bundles before this click, so zeroing the lines would delete
        // what they had, not just what we just did. Report instead.
        var attributable = !wasBump && single &&
          qtyOk(f[0], self.filter, expected) && qtyOk(r[0], self.refill, expected);
        var planFailed = !planOk(f[0], self.planId) || !planOk(r[0], self.planId);

        if (attributable && planFailed) {
          var updates = {};
          updates[f[0].key] = 0;
          updates[r[0].key] = 0;
          return postJSON(root() + 'cart/update.js', { updates: updates }).then(function (undo) {
            if (undo.ok) {
              self.button.disabled = false;
                    self.fail(self.dataset.msgError);
            } else {
              self.button.disabled = true;
              self.fail(self.dataset.msgUnverified);
            }
            return null;
          }, function () {
            self.button.disabled = true;
            self.fail(self.dataset.msgUnverified);
            return null;
          });
        }

        self.button.disabled = true;
        self.fail(self.dataset.msgUnverified);
        return null;
      })
      .catch(function () {
        if (addIssued) {
          // The lines may well be in the cart. Do not tell the shopper otherwise.
          self.button.disabled = true;
          self.fail(self.dataset.msgUnverified);
          return;
        }
        self.button.disabled = false;
        self.fail(self.dataset.msgError);
      })
      .then(release, release);
  };

  // Apply the promo code, then show the price that is actually true.
  //
  // The code is restricted to customers who have ordered before, and that can
  // only be evaluated once Shopify knows who the shopper is - which for someone
  // arriving from an email link is at checkout, not now. So this never asserts
  // the discounted figure: it re-reads the cart and shows the discounted total
  // ONLY if the discount genuinely landed, and otherwise leaves the full price
  // up with a note that it applies at checkout. It can therefore never display
  // an amount the customer will not be charged.
  //
  // Totals are summed from OUR two lines, not from cart.total_price, because the
  // shopper may have unrelated items in the cart.
  Picker.prototype.applyDiscount = function (beforeCodes, beforeCodesRaw) {
    var self = this;
    var code = (this.dataset.discountCode || '').trim();
    if (!code) return Promise.resolve();

    // Never evict a code the shopper already had. /cart/update.js holds one
    // code, so applying ours would silently replace theirs.
    var foreign = beforeCodes.filter(function (d) {
      return String(d.code || '').toUpperCase() !== code.toUpperCase();
    });
    if (foreign.length) return Promise.resolve();

    // T-027(a): did the shopper ALREADY hold our code before we touched the
    // cart? `foreign` being empty does not mean the cart was empty of codes --
    // it means nothing OTHER than ours was on it. A shopper who typed
    // the promo code in themselves lands here, we re-POST the same code
    // redundantly, and without this check we would then claim authorship of
    // THEIR code and later retire it out from under them.
    // Checked against the RAW list, so a code the shopper typed in while the
    // cart was still under the offer's minimum -- which Shopify reports as
    // applicable:false and codesOn() therefore hides -- is still recognised as
    // theirs.
    var alreadyOurs = (beforeCodesRaw || []).some(function (d) {
      return d && String(d.code || '').toUpperCase() === code.toUpperCase();
    });

    return postJSON(root() + 'cart/update.js', { discount: code })
      .then(function (posted) {
        // Authorship is claimed HERE, from the re-read, not from the POST.
        // Three separate things had to be true and each was a review finding:
        //   - the POST actually succeeded (postJSON RESOLVES on HTTP errors
        //     rather than rejecting, so a 422 previously still set the flag);
        //   - the shopper did not already hold this code (alreadyOurs, raw);
        //   - the code is genuinely ON the cart and APPLICABLE afterwards. An
        //     HTTP 200 is not semantic proof a discount took effect, so this
        //     asserts the outcome rather than the request.
        return getJSON(root() + 'cart.js').then(function (res) {
          var landed = res && res.ok && res.body && codesOn(res.body).some(function (d) {
            return String(d.code || '').toUpperCase() === code.toUpperCase();
          });
          if (posted && posted.ok && !alreadyOurs && landed &&
              typeof window.sfbNoteCodeApplied === 'function') {
            // Pass the code itself. The cart snippet stores it and keys its
            // retire off the stored value rather than off its own CODE_KEY --
            // which is a display gate that goes empty the moment the promo
            // becomes applicable:false, i.e. exactly when the bundle leaves.
            window.sfbNoteCodeApplied(code);
          }
          return res;
        });
      })
      .then(function (res) {
        if (!res || !res.ok || !res.body) return;
        var f = linesFor(res.body, self.filter.id)[0];
        var r = linesFor(res.body, self.refill.id)[0];
        if (!f || !r) return;
        var now = Number(f.final_line_price) + Number(r.final_line_price);
        var was = Number(f.original_line_price) + Number(r.original_line_price);
        // The advertised total is PER BUNDLE, so scale it by how many are in
        // the cart before comparing, or two bundles look like a price drift.
        var mult = self.bundleMultiple([f], [r]) || 1;
        self.showTotals(was, now, mult);
      })
      .catch(function () { self.showTotals(null, null); });
  };

  // The card is an ADVERTISEMENT for one bundle. It never mirrors the cart:
  // buying two must not turn the headline into a two-bundle total, and the
  // Liquid-rendered price stays exactly as rendered. All this does is decide
  // whether to show the "discount applies at checkout" note, and warn the
  // operator in the console if the advertised price and the cart disagree.
  Picker.prototype.showTotals = function (was, now, mult) {
    var noteEl = this.querySelector('[data-sfb-note]');
    var discounted = was !== null && now !== null && now < was;

    var perBundle = Number(this.dataset.promoTotal || 0);
    var expected = perBundle * (mult || 1);
    if (perBundle > 0 && now !== null && now !== expected) {
      console.warn('[sf-sub-bundle] advertised ' + perBundle + ' per bundle (' +
        expected + ' for ' + (mult || 1) + ') but the cart charges ' + now +
        '. Update bundle_config.promoTotal or the discount, they no longer agree.');
    }

    if (!noteEl) return;
    if (discounted) {
      noteEl.textContent = '';
      noteEl.hidden = true;
      return;
    }
    // The discount did not land — most likely the shopper is not identified yet
    // — so explain it rather than leaving the advertised price unqualified.
    var msg = this.dataset.msgDiscountPending || '';
    if (msg) { noteEl.textContent = msg; noteEl.hidden = false; }
  };

  Picker.prototype.openCart = function () {
    try {
      if (typeof window.upcartRegisterAddToCart === 'function') {
        window.upcartRegisterAddToCart();
        return;
      }
      if (typeof window.upcartOpenCart === 'function') {
        window.upcartOpenCart();
        return;
      }
      var link = document.querySelector('.header__cart-link a, a[href$="/cart"]');
      if (link) { link.click(); return; }
      window.location = root() + 'cart';
    } catch (e) {
      window.location = root() + 'cart';
    }
  };

  window.customElements.define(TAG, Picker);
})();
